import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiX } from "react-icons/bi";
import { startYear } from "../../../../core/constants/common";
import {
	getCompanyInfoFromStorage,
	getDefaultSelectableYear,
	getMaxSelectableYear,
	getYearDropdownLabel,
	isFinancialYearReporting,
} from "../../../../core/utilities/assessmentPeriodUtils";
import "../Forms.scss";

const STATUS_OPTIONS = [
	{ value: "NOT_STARTED", labelKey: "NotStarted.label" },
	{ value: "IN_PROGRESS", labelKey: "InProgress.label" },
	{ value: "COMPLETED", labelKey: "Completed.label" },
	{ value: "EXPIRED", labelKey: "Expired.label" }
];

function DCEFilterPanel({ t, onApplyFilter, onResetFilter, onFilterHide, initialFilters = {} }) {
	const companyInfo = getCompanyInfoFromStorage();
	const isFinancialYear = isFinancialYearReporting(companyInfo);
	const maxSelectableYear = getMaxSelectableYear();

	const [statusFilters, setStatusFilters] = useState({
		NOT_STARTED: initialFilters.statusFilters?.NOT_STARTED ?? false,
		IN_PROGRESS: initialFilters.statusFilters?.IN_PROGRESS ?? false,
		COMPLETED: initialFilters.statusFilters?.COMPLETED ?? false,
		EXPIRED: initialFilters.statusFilters?.EXPIRED ?? false
	});
	const defaultYearValue = () => String(getDefaultSelectableYear(companyInfo));
	const [year, setYear] = useState(
		initialFilters.year != null && initialFilters.year !== ""
			? String(initialFilters.year)
			: defaultYearValue()
	);

	// Sync state when initialFilters changes (when filter panel reopens)
	// Create a stable string key from filter values for dependency tracking
	const filtersKey = JSON.stringify({
		NOT_STARTED: (initialFilters.statusFilters?.NOT_STARTED ?? false),
		IN_PROGRESS: (initialFilters.statusFilters?.IN_PROGRESS ?? false),
		COMPLETED: (initialFilters.statusFilters?.COMPLETED ?? false),
		EXPIRED: (initialFilters.statusFilters?.EXPIRED ?? false),
		year: initialFilters.year
	});

	// NOSONAR: S6440 - Hooks are called in consistent order, this is a false positive
	useEffect(() => {
		const statusFiltersObj = initialFilters.statusFilters || {};
		setStatusFilters({
			NOT_STARTED: statusFiltersObj.NOT_STARTED ?? false,
			IN_PROGRESS: statusFiltersObj.IN_PROGRESS ?? false,
			COMPLETED: statusFiltersObj.COMPLETED ?? false,
			EXPIRED: statusFiltersObj.EXPIRED ?? false
		});
		if (initialFilters.year !== undefined) {
			setYear(
				initialFilters.year != null && initialFilters.year !== ""
					? String(initialFilters.year)
					: defaultYearValue()
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filtersKey]);

	// Generate year options through current year + 1; FY companies see labels like 2025-2026
	const yearOptions = [];
	for (let i = maxSelectableYear; i >= startYear; i--) {
		yearOptions.push(
			<option value={i} key={i}>{getYearDropdownLabel(i, isFinancialYear)}</option>
		);
	}

	const handleStatusChange = (status) => {
		setStatusFilters(prev => ({
			...prev,
			[status]: !prev[status]
		}));
	};

	const handleSelectAll = () => {
		const allSelected = Object.values(statusFilters).every(val => val);
		setStatusFilters({
			NOT_STARTED: !allSelected,
			IN_PROGRESS: !allSelected,
			COMPLETED: !allSelected,
			EXPIRED: !allSelected
		});
	};

	const applyFilter = () => {
		const selectedStatuses = Object.entries(statusFilters)
			.filter(([_, selected]) => selected)
			.map(([status]) => status);

		onApplyFilter({
			statusFilters: statusFilters,
			submissionStatus: selectedStatuses.length > 0 ? selectedStatuses.join(",") : null,
			year: year !== "" ? Number(year) : null
		});
	};

	const resetFilter = () => {
		setStatusFilters({
			NOT_STARTED: false,
			IN_PROGRESS: true,
			COMPLETED: false,
			EXPIRED: false
		});
		setYear(defaultYearValue());
		onResetFilter();
	};

	const allSelected = Object.values(statusFilters).every(val => val);

	return (
		<div className="DCEFilterPanel_Eventcard">
			<div className="DCEFilterPanel_Eventcard_head">
				<h5>{t("Filter.label")}</h5>
				<div 
					className="btnClose" 
					onClick={() => onFilterHide(false)}>
					<BiX />
				</div>
			</div>
			<div className="DCEFilterPanel_Eventcard_body">
				<div className="row m-0">
					<div className="col-12 p-2">
						<div className="mb-3">
							<h6 className="mb-2">{t("Status.label")}</h6>
							{/* <Button
								variant="primary"
								size="sm"
								className="mb-2"
								onClick={handleSelectAll}
							>
								{allSelected ? t("DeselectAll.label") : t("SelectAll.label")}
							</Button> */}
							<Form.Check
								type="checkbox"
								id="status-checkbox-all"
								label={allSelected ? t("DeselectAll.label") : t("SelectAll.label")}
								checked={allSelected}
								onChange={handleSelectAll}
								className="mb-1"
							/>
							{STATUS_OPTIONS.map((option) => (
								<Form.Check
									key={option.value}
									type="checkbox"
									id={`status-${option.value}`}
									label={t(option.labelKey)}
									checked={statusFilters[option.value]}
									onChange={() => handleStatusChange(option.value)}
								/>
							))}
						</div>
					</div>
					<div className="col-12 p-2">
						<div className="mb-3">
							<h6 className="mb-2">{t("Year.label")}</h6>
							<Form.Select
								value={year}
								onChange={(e) => setYear(e.target.value)}
							>
								<option value="">{t("SelectYear.label")}</option>
								{yearOptions}
							</Form.Select>
						</div>
					</div>
				</div>
			</div>
			<div className="DCEFilterPanel_Eventcard_footer p-2 pb-3 text-center">
				<Button variant="primary"
					onClick={applyFilter}
					className="sbmBtn px-4">
					{t("Apply.label")}
				</Button>
				<Button variant="secondary"
					onClick={resetFilter}
					className="px-4 mx-2">
					{t("Reset.label")}
				</Button>
			</div>
		</div>
	);
}

DCEFilterPanel.propTypes = {};

export default withTranslation()(DCEFilterPanel);

